import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Loader2, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "./AdminLayout";
import { Card, Pagination, StatusPill, fmtDateTime, EmptyState } from "./_shared";

const LIMIT = 25;
const STATUS_OPTIONS = ["", "draft", "submitted", "processed"];

export default function AdminAssessments() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/assessments", {
          params: {
            q: q || undefined,
            status: status || undefined,
            skip,
            limit: LIMIT,
          },
        });
        if (!cancelled) setData(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q, status, skip]);

  return (
    <AdminLayout
      title="Assessments"
      subtitle="Every candidate's 12-step assessment with AI report status."
    >
      <div className="space-y-5">
        <Card className="p-4" testid="admin-assessments-filters">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2 relative">
              <Search className="h-4 w-4 text-slate-400 absolute left-3 top-3" />
              <input
                value={q}
                onChange={(e) => {
                  setSkip(0);
                  setQ(e.target.value);
                }}
                placeholder="Search candidate email or name..."
                className="w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                data-testid="admin-assessments-search-input"
              />
            </div>
            <select
              value={status}
              onChange={(e) => {
                setSkip(0);
                setStatus(e.target.value);
              }}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
              data-testid="admin-assessments-status-select"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s ? `Status: ${s}` : "All statuses"}
                </option>
              ))}
            </select>
          </div>
        </Card>

        <Card className="overflow-hidden" testid="admin-assessments-table-card">
          {loading ? (
            <div className="flex items-center gap-2 px-5 py-6 text-sm text-slate-500" data-testid="admin-assessments-loading">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading...
            </div>
          ) : data.items.length === 0 ? (
            <EmptyState
              title="No assessments match"
              body="Try a different search or status."
              testid="admin-assessments-empty"
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="text-left px-5 py-2.5 font-medium">Candidate</th>
                    <th className="text-left px-5 py-2.5 font-medium">Current role</th>
                    <th className="text-left px-5 py-2.5 font-medium">Status</th>
                    <th className="text-left px-5 py-2.5 font-medium">AI report</th>
                    <th className="text-left px-5 py-2.5 font-medium">Updated</th>
                    <th className="px-5 py-2.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-testid="admin-assessments-tbody">
                  {data.items.map((it) => (
                    <tr
                      key={it.assessment_id || it.user_id}
                      className="hover:bg-slate-50"
                      data-testid={`admin-assessments-row-${it.user_id}`}
                    >
                      <td className="px-5 py-3">
                        <Link
                          to={`/admin/assessments/${it.user_id}`}
                          className="block group"
                          data-testid={`admin-assessments-name-link-${it.user_id}`}
                        >
                          <div className="font-medium text-slate-900 group-hover:text-sky-700">
                            {it.user?.name || it.user?.email?.split("@")[0] || "—"}
                          </div>
                          <div className="text-xs text-slate-500">{it.user?.email || "—"}</div>
                        </Link>
                      </td>
                      <td className="px-5 py-3 text-slate-600">
                        {it.current_role || "—"}
                        <div className="text-xs text-slate-400">{it.industry || ""}</div>
                      </td>
                      <td className="px-5 py-3">
                        <StatusPill
                          value={it.status}
                          tone={
                            it.status === "processed"
                              ? "green"
                              : it.status === "submitted"
                                ? "sky"
                                : "slate"
                          }
                        />
                      </td>
                      <td className="px-5 py-3 text-slate-600">
                        {it.has_ai_report ? "Yes" : "—"}
                      </td>
                      <td className="px-5 py-3 text-slate-500">{fmtDateTime(it.updated_at)}</td>
                      <td className="px-5 py-3 text-right">
                        <Link
                          to={`/admin/assessments/${it.user_id}`}
                          className="inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:text-sky-900"
                          data-testid={`admin-assessments-view-${it.user_id}`}
                        >
                          Open <ChevronRight className="h-3 w-3" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!loading && data.items.length > 0 && (
            <div className="px-5 py-3 border-t border-slate-100">
              <Pagination
                total={data.total}
                skip={skip}
                limit={LIMIT}
                onChange={setSkip}
                testidPrefix="admin-assessments"
              />
            </div>
          )}
        </Card>
      </div>
    </AdminLayout>
  );
}
